import React from "react";
import * as Icon from "react-feather";

import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";

import ToolsCollapsiblePanel from "../kit/ToolsCollapsiblePanel";
import ToolsMappingRow from "../kit/ToolsMappingRow";
import ToolsNotice from "../kit/ToolsNotice";
import ToolsProgressBadge from "../kit/ToolsProgressBadge";

import "./pairings-panel.css";

class PairingsPanel extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isSaving: false,
			rows: [],
			options: [],
			errorMessage: ""
		};
	}

	async componentDidMount() {
		await this.load();
	}

	load = async () => {
		const { loadRows } = this.props;

		await this.update({ isLoading: true, errorMessage: "" });

		const result = await loadRows();

		if (result.error) {
			await this.update({ isLoading: false, errorMessage: result.error });
			return;
		}

		await this.update({ isLoading: false, rows: result.rows, options: result.options });
	};

	savePairings = async ({ pairings }) => {
		const { saveRows, onChanged } = this.props;

		if (pairings.length === 0) {
			return;
		}

		await this.update({ isSaving: true });
		const result = await saveRows({ pairings });
		await this.update({ isSaving: false });

		if (result.error) {
			UtilityService.alert({ type: "error", text: result.error });
			return;
		}

		await this.load();

		if (onChanged) {
			await onChanged();
		}
	};

	onOptionChosen = async ({ row, optionId }) => {
		const { options } = this.state;
		const chosen = options.find(option => option.id === optionId);

		await this.savePairings({ pairings: [{ row, id: optionId, name: chosen ? chosen.name : null }] });
	};

	onSuggestionAccepted = async ({ row }) => {
		await this.savePairings({ pairings: [{ row, id: row.suggestion.id, name: row.suggestion.name }] });
	};

	onAllSuggestionsAccepted = async () => {
		const { rows } = this.state;

		const pairings = rows.filter(row => !row.chosenId && row.suggestion).map(row => ({ row, id: row.suggestion.id, name: row.suggestion.name }));

		await this.savePairings({ pairings });
	};

	renderRow = ({ row }) => {
		const { options, isSaving } = this.state;
		const { placeholder, searchPlaceholder, certaintyLabel } = this.props;

		const choices = options.map(option => ({ value: option.id, label: option.name }));
		const suggestion = row.suggestion
			? { value: row.suggestion.id, label: row.suggestion.name, evidence: row.suggestion.evidence, isCertain: row.suggestion.isCertain, certaintyLabel }
			: null;

		return (
			<ToolsMappingRow
				key={row.key}
				isChosen={Boolean(row.chosenId)}
				label={row.label}
				sublabel={row.sublabel}
				amount={row.amount}
				options={choices}
				value={row.chosenId}
				placeholder={placeholder}
				searchPlaceholder={searchPlaceholder}
				disabled={isSaving}
				onChange={optionId => this.onOptionChosen({ row, optionId })}
				suggestion={suggestion}
				onAcceptSuggestion={() => this.onSuggestionAccepted({ row })}
			/>
		);
	};

	render() {
		const { isLoading, rows, options, errorMessage } = this.state;
		const { title, lede, loadingText, doneWord, todoWord, noOptionsNotice } = this.props;

		if (isLoading) {
			return (
				<div className="tk-panel tk-panel-loading">
					<Loader />
					<span className="tk-text-muted">{loadingText}</span>
				</div>
			);
		}

		const requiredRows = rows.filter(row => row.isRequired);
		const unpairedRequired = requiredRows.filter(row => !row.chosenId);
		const suggestable = rows.filter(row => !row.chosenId && row.suggestion);
		const wasRead = !errorMessage;
		const hasNoOptions = wasRead && options.length === 0;

		return (
			<ToolsCollapsiblePanel
				title={title}
				defaultOpen={unpairedRequired.length > 0}
				status={<ToolsProgressBadge remainingCount={unpairedRequired.length} totalCount={requiredRows.length} doneWord={doneWord} todoWord={todoWord} />}
			>
				<div className="tk-lede">{lede}</div>

				{hasNoOptions && noOptionsNotice && (
					<ToolsNotice
						tone="warning"
						icon={<Icon.AlertTriangle size={16} />}
						title={noOptionsNotice.title}
						detail={noOptionsNotice.detail}
						className="pairings-panel__notice"
					/>
				)}

				{suggestable.length > 0 && (
					<div className="tk-suggest-bar">
						<span className="tk-suggest-bar__text">{suggestable.length} of the unpaired rows have a likely match in QuickBooks. Check each before accepting.</span>
						<button className="tk-button tk-button--secondary tk-button--compact" onClick={this.onAllSuggestionsAccepted} type="button">
							Use all {suggestable.length}
						</button>
					</div>
				)}

				<div>{rows.map(row => this.renderRow({ row }))}</div>

				{errorMessage && <div className="pairings-panel__error tk-text-muted">{errorMessage}</div>}
			</ToolsCollapsiblePanel>
		);
	}
}

export default PairingsPanel;
